import { useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { disputeAPI } from '../lib/api';
import { toast } from 'sonner';

const USER_REASONS = [
  { value: 'driver_no_show', label: 'Driver no-show' },
  { value: 'damaged_goods', label: 'Damaged goods / items' },
  { value: 'late_delivery', label: 'Excessively late' },
  { value: 'wrong_items', label: 'Wrong items delivered' },
  { value: 'unprofessional_behavior', label: 'Unprofessional behavior' },
  { value: 'safety_concern', label: 'Safety concern' },
  { value: 'billing_error', label: 'Billing error' },
  { value: 'other', label: 'Other' },
];

const DRIVER_REASONS = [
  { value: 'user_no_show', label: 'User no-show' },
  { value: 'user_abusive', label: 'User was abusive / unsafe' },
  { value: 'billing_error', label: 'Billing / payout error' },
  { value: 'damaged_goods', label: 'Item damaged en route (not driver fault)' },
  { value: 'other', label: 'Other' },
];

const DisputeDialog = ({ open, onOpenChange, job, userRole, onCreated }) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasonOptions = userRole === 'driver' ? DRIVER_REASONS : USER_REASONS;

  const handleSubmit = async () => {
    if (!reason) { toast.error('Please pick a reason'); return; }
    if (description.trim().length < 10) {
      toast.error('Please describe the issue (10+ chars)');
      return;
    }
    setSubmitting(true);
    try {
      await disputeAPI.create({
        job_id: job.id,
        reason,
        description: description.trim(),
      });
      toast.success('Dispute submitted. Our team will review within 1-2 business days.');
      setReason('');
      setDescription('');
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit dispute');
    } finally {
      setSubmitting(false);
    }
  };

  if (!job) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dispute-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Report an Issue
          </DialogTitle>
          <DialogDescription>
            We're sorry something went wrong. Tell us what happened and our team will review within 1-2 business days. Disputes must be filed within 7 days of completion.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-zinc-50 rounded-lg p-3 text-xs">
            <p className="text-zinc-500">Job</p>
            <p className="font-medium text-zinc-900 truncate">{job.pickup_address} → {job.dropoff_address}</p>
            <p className="text-zinc-500 mt-1">Amount: <span className="font-semibold text-zinc-900">${(job.price || 0).toFixed(2)}</span></p>
          </div>

          <div>
            <Label>Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="dispute-reason-select">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent>
                {reasonOptions.map(r => (
                  <SelectItem key={r.value} value={r.value} data-testid={`dispute-reason-${r.value}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Describe the issue *</Label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What happened? Include any details that will help us investigate."
              rows={4}
              maxLength={1000}
              data-testid="dispute-description-input"
            />
            <p className="text-xs text-zinc-500 mt-1">{description.length}/1000</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="submit-dispute-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
            Submit Dispute
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DisputeDialog;
